/*
 * VanArtHub: move the carousel arrows onto the picture on phones and tablets.
 *
 * Why this needs JavaScript at all: the arrows have to line up with the middle
 * of the PICTURE, and the picture is a different height on every slide. The
 * homepage row uses the "adapt" image ratio, so a portrait work and a landscape
 * work in the same carousel differ by well over 100px. CSS cannot read that, so
 * this measures the current slide and writes two custom properties that
 * `assets/component-slider.css` positions the bar with.
 *
 * The class `.vah-arrows-over` is only added once a real measurement exists.
 * Until then, and if this file never loads, the control bar stays exactly where
 * it was, under the list. There is no broken middle state.
 *
 * Scope: sliders that put their controls in a `.slider-buttons` bar and swipe on
 * touch (`.slider--tablet` / `.slider--mobile`): the homepage product row, the
 * collection row, multicolumn, featured blog, and the product gallery. The
 * slideshow and the announcement bar are `.slider--everywhere` and draw their own
 * controls, so they are untouched. The product thumbnail strip is untouched too:
 * its buttons are direct children, not wrapped in a `.slider-buttons` bar.
 */
(function () {
  // First match wins, so the more specific card wrappers come before `.media`.
  var MEDIA_SELECTOR = '.card__inner, .card__media, .product__media, .global-media-settings, .media';
  var groups = [];

  function collect() {
    groups = [];
    document.querySelectorAll('.slider-buttons').forEach(function (bar) {
      var list = bar.previousElementSibling;
      if (!list || !list.classList.contains('slider')) return;
      if (!list.classList.contains('slider--tablet') && !list.classList.contains('slider--mobile')) return;
      var component = bar.parentElement;
      if (!component) return;
      groups.push({ component: component, list: list, bar: bar });
    });
  }

  // The slide a visitor is looking at. These rows are `grid--peek`, so the
  // current card is aligned to the left edge of the list and the next one peeks
  // in from the right. Nearest left edge is therefore the right test, not
  // nearest centre.
  function currentSlide(list) {
    var slides = list.children;
    if (!slides.length) return null;
    var listLeft = list.getBoundingClientRect().left;
    var best = null;
    var bestDistance = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var distance = Math.abs(slides[i].getBoundingClientRect().left - listLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = slides[i];
      }
    }
    return best;
  }

  // How far through the row we are, 0 to 1. On a wide screen this replaces the
  // "1 / 8" label, which says almost nothing when the row scrolls a page at a
  // time. The element is created here rather than in Liquid so that a row with
  // nothing to scroll never grows one.
  function progress(group) {
    var scrollable = group.list.scrollWidth - group.list.clientWidth;
    // Below 990px the control bar is hidden on some of these rows (a tablet
    // slider becomes a plain grid on desktop, for one), and a progress bar under
    // a grid that does not scroll would be nonsense.
    var live = scrollable > 4 && getComputedStyle(group.bar).display !== 'none';
    if (!live) {
      if (group.progress) group.progress.hidden = true;
      return;
    }
    if (!group.progress) {
      var track = document.createElement('div');
      track.className = 'vah-slider-progress';
      track.setAttribute('aria-hidden', 'true');
      track.appendChild(document.createElement('span'));
      group.component.appendChild(track);
      group.progress = track;
    }
    group.progress.hidden = false;
    var ratio = Math.min(1, Math.max(0, group.list.scrollLeft / scrollable));
    group.progress.style.setProperty('--vah-progress', ratio.toFixed(3));
  }

  function measure(group) {
    var slide = currentSlide(group.list);
    var media = slide && slide.querySelector(MEDIA_SELECTOR);
    if (!media) return; // nothing to sit on: leave the bar where it is

    var mediaBox = media.getBoundingClientRect();
    // An image that has not loaded yet reports almost nothing. Measuring that
    // would pin the arrows to the top of the card, so wait for a real height.
    if (mediaBox.height < 80) return;

    var componentBox = group.component.getBoundingClientRect();
    group.component.style.setProperty('--vah-media-top', Math.round(mediaBox.top - componentBox.top) + 'px');
    group.component.style.setProperty('--vah-media-height', Math.round(mediaBox.height) + 'px');
    group.component.classList.add('vah-arrows-over');
    progress(group);
  }

  var frame = null;
  function measureAll() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      groups.forEach(measure);
    });
  }

  function watch(group) {
    // Swiping changes which slide is current, so the arrows follow it.
    group.list.addEventListener('scroll', measureAll, { passive: true });
    // Lazy images arrive after first paint and change the height they sit in.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(measureAll).observe(group.list);
    }
  }

  function start() {
    collect();
    groups.forEach(watch);
    measureAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('load', measureAll);
  window.addEventListener('resize', measureAll, { passive: true });
  window.addEventListener('orientationchange', measureAll);
  // The row is laid out differently either side of 990px, so re-measure when a
  // window crosses that line rather than waiting for the next scroll.
  var breakpoint = window.matchMedia('(min-width: 990px)');
  if (typeof breakpoint.addEventListener === 'function') breakpoint.addEventListener('change', measureAll);

  // Sections re-render on their own in the theme editor and after a cart or
  // section update, which replaces these nodes. Re-collect when that happens.
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:reorder', start);
})();
